import { useState, useCallback } from 'react';
import { api, AdminUserPaymentDetail, AdminUserLookupResult } from '@/lib/api';

export function CustomerLookupTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<AdminUserLookupResult | null>(null);
  const [paymentDetail, setPaymentDetail] = useState<AdminUserPaymentDetail | null>(null);

  // Grant state
  const [grantAmount, setGrantAmount] = useState('');
  const [grantDescription, setGrantDescription] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantResult, setGrantResult] = useState<string | null>(null);

  // Deduct state
  const [deductAmount, setDeductAmount] = useState('');
  const [deductBucket, setDeductBucket] = useState('fias');
  const [deductReason, setDeductReason] = useState('');
  const [deductLoading, setDeductLoading] = useState(false);
  const [deductResult, setDeductResult] = useState<string | null>(null);

  // Refund state
  const [refundPaymentId, setRefundPaymentId] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundResult, setRefundResult] = useState<string | null>(null);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setError(null);
    setUserInfo(null);
    setPaymentDetail(null);
    setGrantResult(null);
    setDeductResult(null);
    setRefundResult(null);

    try {
      const user = await api.adminLookupUserByAnyIdentifier(searchQuery.trim());
      setUserInfo(user);
      const detail = await api.adminGetUserPaymentDetail(user.userId);
      setPaymentDetail(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'User not found');
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  const refreshUserDetail = useCallback(async () => {
    if (!userInfo) return;
    try {
      const detail = await api.adminGetUserPaymentDetail(userInfo.userId);
      setPaymentDetail(detail);
    } catch {
      // Silently fail on refresh
    }
  }, [userInfo]);

  const handleGrant = async () => {
    if (!userInfo || !grantAmount) return;
    setGrantLoading(true);
    setGrantResult(null);
    try {
      const result = await api.adminGrantCredits(
        userInfo.userId,
        parseFloat(grantAmount),
        grantDescription.trim() || undefined,
      );
      setGrantResult(`Granted. New balance: $${(result.newBalance / 100).toFixed(2)}`);
      setGrantAmount('');
      setGrantDescription('');
      refreshUserDetail();
    } catch (err) {
      setGrantResult(`Error: ${err instanceof Error ? err.message : 'Failed'}`);
    } finally {
      setGrantLoading(false);
    }
  };

  const handleDeduct = async () => {
    if (!userInfo || !deductAmount || !deductReason.trim()) return;
    setDeductLoading(true);
    setDeductResult(null);
    try {
      const result = await api.adminDeductCredits(
        userInfo.userId,
        parseFloat(deductAmount),
        deductBucket,
        deductReason.trim(),
      );
      setDeductResult(`Deducted. New balance: $${(result.newBalance / 100).toFixed(2)}`);
      setDeductAmount('');
      setDeductReason('');
      refreshUserDetail();
    } catch (err) {
      setDeductResult(`Error: ${err instanceof Error ? err.message : 'Failed'}`);
    } finally {
      setDeductLoading(false);
    }
  };

  const handleRefund = async (paymentId: string) => {
    setRefundPaymentId(paymentId);
  };

  const processRefund = async () => {
    if (!refundPaymentId.trim()) return;
    setRefundLoading(true);
    setRefundResult(null);
    try {
      await api.adminProcessRefund(refundPaymentId.trim(), refundReason.trim() || undefined);
      setRefundResult('Refund processed successfully');
      setRefundPaymentId('');
      setRefundReason('');
      refreshUserDetail();
    } catch (err) {
      setRefundResult(`Error: ${err instanceof Error ? err.message : 'Failed'}`);
    } finally {
      setRefundLoading(false);
    }
  };

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      succeeded: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      refunded: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      canceled: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
      past_due: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    return (
      <span
        className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[status] || 'bg-muted text-muted-foreground'}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div>
      {/* Search */}
      <div className="flex gap-3 mb-6">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Search by username, email, or user ID..."
          className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
        />
        <button
          onClick={handleSearch}
          disabled={searching || !searchQuery.trim()}
          className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          {searching ? 'Searching...' : 'Search'}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {userInfo && paymentDetail && (
        <>
          {/* User Info */}
          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-3">User Profile</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Name</p>
                <p className="font-medium text-foreground">{userInfo.displayName || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Username</p>
                <p className="font-medium text-foreground">{userInfo.username}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Email</p>
                <p className="font-medium text-foreground break-all">{userInfo.email || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">User ID</p>
                <p className="font-mono text-xs text-foreground break-all">{userInfo.userId}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Account Status</p>
                <div className="flex items-center gap-2">{statusBadge(userInfo.status)}</div>
              </div>
              <div>
                <p className="text-muted-foreground">Roles</p>
                <p className="text-foreground">{userInfo.roles.join(', ') || 'none'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Auth Method</p>
                <p className="text-foreground capitalize">{userInfo.primaryAuthMethod || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Subscription</p>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground capitalize">
                    {paymentDetail.subscription.tier}
                  </span>
                  {statusBadge(paymentDetail.subscription.status)}
                </div>
              </div>
              <div>
                <p className="text-muted-foreground">Contributor</p>
                <p className="text-foreground capitalize">{userInfo.contributorStatus || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Payments Enabled</p>
                <p className="text-foreground">
                  {userInfo.paymentEnabled == null ? '—' : userInfo.paymentEnabled ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">DevvX Linked</p>
                <p className="text-foreground">
                  {userInfo.devvxAccountLinked == null
                    ? '—'
                    : userInfo.devvxAccountLinked
                      ? `Yes${userInfo.devvxAccountId ? ` (${userInfo.devvxAccountId})` : ''}`
                      : 'No'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Joined</p>
                <p className="text-foreground">{formatDate(userInfo.createdAt)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Last Updated</p>
                <p className="text-foreground">{formatDate(userInfo.updatedAt)}</p>
              </div>
            </div>
          </div>

          {/* Credit Balances */}
          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-3">Credit Balances</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Total Balance</p>
                <p className="text-2xl font-bold text-foreground">
                  ${(paymentDetail.credits.balance / 100).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Lifetime Used</p>
                <p className="text-lg font-medium text-foreground">
                  ${(paymentDetail.credits.lifetimeUsed / 100).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Lifetime Granted</p>
                <p className="text-lg font-medium text-foreground">
                  ${(paymentDetail.credits.lifetimeGranted / 100).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">System Credits Granted</p>
                <p className="text-lg font-medium text-foreground">
                  ${(paymentDetail.systemCreditsGranted / 100).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Admin Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Grant Credits */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-sm font-semibold text-foreground mb-3">Grant Credits</h3>
              <div className="space-y-2">
                <input
                  type="number"
                  value={grantAmount}
                  onChange={(e) => setGrantAmount(e.target.value)}
                  placeholder="Amount (credits)"
                  min="0.01"
                  step="0.01"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                />
                <input
                  type="text"
                  value={grantDescription}
                  onChange={(e) => setGrantDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                />
                <button
                  onClick={handleGrant}
                  disabled={grantLoading || !grantAmount}
                  className="w-full px-3 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  {grantLoading ? 'Granting...' : 'Grant Credits'}
                </button>
                {grantResult && (
                  <p
                    className={`text-sm ${grantResult.startsWith('Error') ? 'text-destructive' : 'text-green-600'}`}
                  >
                    {grantResult}
                  </p>
                )}
              </div>
            </div>

            {/* Deduct Credits */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-sm font-semibold text-foreground mb-3">Deduct Credits</h3>
              <div className="space-y-2">
                <input
                  type="number"
                  value={deductAmount}
                  onChange={(e) => setDeductAmount(e.target.value)}
                  placeholder="Amount (credits)"
                  min="0.01"
                  step="0.01"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                />
                <select
                  value={deductBucket}
                  onChange={(e) => setDeductBucket(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                >
                  <option value="fias">Fias (purchased)</option>
                  <option value="subscription">Subscription</option>
                  <option value="system">System</option>
                </select>
                <input
                  type="text"
                  value={deductReason}
                  onChange={(e) => setDeductReason(e.target.value)}
                  placeholder="Reason (required)"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                />
                <button
                  onClick={handleDeduct}
                  disabled={deductLoading || !deductAmount || !deductReason.trim()}
                  className="w-full px-3 py-2 text-sm font-medium text-destructive-foreground bg-destructive rounded-lg hover:bg-destructive/90 disabled:opacity-50"
                >
                  {deductLoading ? 'Deducting...' : 'Deduct Credits'}
                </button>
                {deductResult && (
                  <p
                    className={`text-sm ${deductResult.startsWith('Error') ? 'text-destructive' : 'text-green-600'}`}
                  >
                    {deductResult}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Payments */}
          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-3">Recent Payments</h3>
            {paymentDetail.recentPayments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No payment history</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-muted-foreground font-medium">
                        Payment ID
                      </th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Amount</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Status</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Date</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentDetail.recentPayments.map((payment) => (
                      <tr key={payment.paymentId} className="border-b border-border/50">
                        <td className="py-2 text-foreground font-mono text-xs">
                          {payment.paymentId}
                        </td>
                        <td className="py-2 text-foreground">
                          ${(payment.amountCents / 100).toFixed(2)}
                        </td>
                        <td className="py-2">{statusBadge(payment.status)}</td>
                        <td className="py-2 text-muted-foreground">
                          {new Date(payment.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-2">
                          {payment.status === 'succeeded' && (
                            <button
                              onClick={() => handleRefund(payment.paymentId)}
                              className="text-xs text-destructive hover:underline"
                            >
                              Refund
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Refund Form (shown when refund button clicked) */}
          {refundPaymentId && (
            <div className="bg-card rounded-lg border border-destructive/30 p-6 mb-6">
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Process Refund: {refundPaymentId}
              </h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Reason (optional)"
                  className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                />
                <button
                  onClick={processRefund}
                  disabled={refundLoading}
                  className="px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive rounded-lg hover:bg-destructive/90 disabled:opacity-50"
                >
                  {refundLoading ? 'Processing...' : 'Confirm Refund'}
                </button>
                <button
                  onClick={() => {
                    setRefundPaymentId('');
                    setRefundReason('');
                    setRefundResult(null);
                  }}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
              {refundResult && (
                <p
                  className={`mt-2 text-sm ${refundResult.startsWith('Error') ? 'text-destructive' : 'text-green-600'}`}
                >
                  {refundResult}
                </p>
              )}
            </div>
          )}

          {/* Recent Credit Transactions */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-3">
              Recent Credit Transactions
            </h3>
            {paymentDetail.recentTransactions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No transactions</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-muted-foreground font-medium">Type</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Amount</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Bucket</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">
                        Description
                      </th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentDetail.recentTransactions.map((tx) => (
                      <tr key={tx.transactionId} className="border-b border-border/50">
                        <td className="py-2 text-foreground">{tx.type}</td>
                        <td
                          className={`py-2 font-mono ${tx.amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}
                        >
                          {tx.amount < 0 ? '-' : '+'}${Math.abs(tx.amount / 100).toFixed(2)}
                        </td>
                        <td className="py-2 text-muted-foreground">{tx.bucket || '-'}</td>
                        <td className="py-2 text-muted-foreground truncate max-w-xs">
                          {tx.description}
                        </td>
                        <td className="py-2 text-muted-foreground">
                          {new Date(tx.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
